*,
*::before,
*::after {
  box-sizing: border-box;
}

/* Global variables:
// for :host
  var(--modal-backdrop-bg);
  var(--modal-backdrop-opacity);

// for modal
  var(--modal-align);
  var(--modal-padding-x);
  var(--modal-padding-y);
  var(--modal-radius);
  var(--modal-bg);
  var(--modal-color);
  var(--modal-header-margin);
  var(--modal-header-gap);
  var(--modal-footer-margin);
  var(--modal-close-icon-position-top);
  var(--modal-close-icon-position-right);
  var(--modal-title-font-size);
  var(--modal-title-font-weight);
  var(--modal-text-font-size);
  var(--modal-text-line-height);
  var(--modal-text-color);
  var(--modal-buttons-gap);
  var(--modal-buttons-padding);
*/

:host {
  --_anim-duration: var(--ui-modal-animation-duration, 250ms);
  --_grid-template-columns: [full-start] 0 [main-start] 1fr [main-end] 0 [full-end];
  --_grid-template-rows: [full-start] 1rem [main-start] 1fr [main-end full-end];
  --_page-padding-inline: 16px;

  display: grid;
  align-items: end;
  grid-template-columns: var(--_grid-template-columns);
  grid-template-rows: var(--_grid-template-rows);
  position: fixed;
  inset: 0;
  z-index: var(--z-index-modal);
}

@media (min-width: 768px) {
  :host {
    --_grid-template-columns: [full-start] 1fr [main-start] 720px [main-end] 1fr [full-end];
  }
}

@media (min-width: 1024px) {
  :host {
    --_grid-template-columns: [full-start] 1fr [main-start] 480px [main-end] 1fr [full-end];
    --_grid-template-rows: [full-start] var(--_page-padding-inline) [main-start]
      calc(100dvh - var(--_page-padding-inline) * 2) [main-end] var(--_page-padding-inline) [full-end];
    --_anim-duration: 0.1s;

    align-items: center;
    margin: auto;
  }

  :host(.modal--wide) {
    --_grid-template-columns: [full-start] 1fr [main-start] 650px [main-end] 1fr [full-end];
  }

  /* Native 5G wheel-of-fortune game. The default 480px column renders the
   * portrait game as a narrow strip lost in the desktop gutters; a wider
   * portrait column gives the wheel and its chrome room to breathe. Scoped to
   * this modifier so the Gamanza iframe mini-game (which shares
   * .modal--incentive-mini-game and is designed for the narrow format) is
   * unaffected. The game itself scales up via --wof-max-size in the 5G modal's
   * own stylesheet. */
  :host(.modal--incentive-mini-game-5g) {
    --_grid-template-columns: [full-start] 1fr [main-start] 620px [main-end] 1fr [full-end];
  }
}

.modal {
  --_open-delay: 0.3s; /* Pause to allow for content to render */

  animation: var(--_anim-duration) slide-up var(--_open-delay) ease-out forwards;
  background: var(--modal-bg);
  color: var(--modal-color);
  border-radius: var(--modal-radius) var(--modal-radius) 0 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  grid-column: main;
  grid-row: main;
  max-height: calc(100dvh - 32px);
  min-height: 0;
  overflow: hidden;
  padding: var(--modal-padding-y) var(--modal-padding-x) 0;
  text-align: var(--modal-align);
  transform: translateY(100%);
  z-index: 1;
}

@media (min-width: 1024px) {
  .modal {
    animation: fade-in var(--_anim-duration) ease-out forwards;
    border-radius: var(--modal-radius);
    transform: translateY(0);
  }
}

.modal-backdrop {
  animation: fade-in var(--_anim-duration) forwards;
  background: var(--modal-backdrop-bg);
  opacity: var(--modal-backdrop-opacity);
  inset: 0;
  position: fixed;
  z-index: 0;
}

.modal-backdrop--closed {
  animation: fade-out var(--_anim-duration) forwards;
  pointer-events: none;
}

.modal-backdrop--closed + .modal {
  animation: slide-down var(--_anim-duration) ease-in-out forwards;
}

@media (min-width: 1024px) {
  .modal-backdrop--closed + .modal {
    animation: fade-out var(--_anim-duration) ease-in-out forwards;
  }
}

.close-icon {
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  min-height: 44px;
  padding: var(--ui-space-xs);
  position: absolute;
  right: var(--modal-close-icon-position-right);
  top: var(--modal-close-icon-position-top);
  -webkit-tap-highlight-color: transparent;
}

.content-wrapper {
  overflow: auto;
  overscroll-behavior: contain;
}

.modal--wide .content-wrapper {
  height: 100%;
}

.modal-header {
  display: flex;
  flex-direction: column;
  align-items: var(--modal-align);
  gap: var(--modal-header-gap);
  margin: var(--modal-header-margin);
}

.modal-header--empty {
  display: none;
}

.modal-title {
  width: 100%;
  font-size: var(--modal-title-font-size);
  font-weight: var(--modal-title-font-weight);
}

.modal-main {
  color: var(--modal-text-color);
  font-size: var(--modal-text-font-size);
  line-height: var(--modal-text-line-height);
  padding-bottom: var(--modal-padding-y);
  display: block;
  flex: 1 1 auto;
  text-wrap: balance;
}

.modal--wide .modal-main {
  height: 100%;
}

.modal--incentive .modal-main {
  display: flex;
  flex-direction: column;
}

.modal-footer {
  display: block;
  margin: var(--modal-footer-margin);
}

.modal-footer--incentive {
  margin: 0;
}

.modal-buttons {
  display: flex;
  flex-direction: column;
  gap: var(--modal-buttons-gap);
  padding: var(--modal-buttons-padding);
  width: 100%;
  position: relative;
}

.modal-button {
  flex: 1 1 50%;
}

.modal-buttons::before {
  content: "";
  position: absolute;
  top: calc(var(--modal-padding-y) * -1);
  left: 0;
  width: 100%;
  height: var(--modal-padding-y);
  background: linear-gradient(to top, var(--modal-bg) 0%, rgb(39 39 39 / 0) 100%);
}

.modal--actions-row .modal-buttons {
  flex-direction: row;
}

@media screen and (max-height: 430px) and (orientation: landscape) {
  .modal-buttons {
    flex-direction: row;
  }
}

/* animations */

@keyframes slide-up {
  to {
    transform: translateY(0);
  }
}

@keyframes slide-down {
  from {
    transform: translateY(0);
  }

  to {
    transform: translateY(100%);
  }
}

@keyframes fade-in {
  from {
    opacity: 0;
  }
}

@keyframes fade-out {
  to {
    opacity: 0;
  }
}
:host {
  --modal-backdrop-bg: var(--primary-dark);
}

.modal {
  --modal-align: start;
  --modal-padding-x: var(--ui-space-xl);
  --modal-padding-y: 40px;
  --modal-radius: var(--ui-border-radius-xl);
  --modal-title-font-size: var(--ui-font-size-xl);
  --modal-title-font-weight: var(--ui-font-weight-semi-bold);
  --modal-close-icon-position-top: 8px;
  --modal-close-icon-position-right: 0;
  --modal-text-font-size: var(--ui-font-size-s);
  --modal-text-line-height: var(--ui-line-height-base);
  --modal-text-color: var(--ui-color-gray-10);
  --modal-header-margin: 0 0 var(--ui-space-xs);
  --modal-header-gap: var(--ui-space-lg);
  --modal-footer-margin: 0 0 var(--ui-space-lg);
  --modal-buttons-gap: var(--ui-space-sm);
  --modal-buttons-padding: 0 0 var(--ui-space-xl);
}

@media screen and (max-height: 430px) and (orientation: landscape) {
  .modal {
    --modal-padding-y: var(--ui-space-xl);
    --modal-header-gap: var(--ui-space-md);
    --modal-buttons-padding: 0 0 var(--ui-space-xs);
    --modal-footer-margin: 0 0 var(--ui-space-sm);
  }
}

.modal--centered {
  --modal-align: center;
}

.modal.modal--wide {
  --modal-padding-x: 0;
  --modal-padding-y: 0;

  height: 100%;
}

.modal.modal--incentive {
  --modal-footer-margin: 0;
  --modal-bg: var(--ui-color-base);
}

.modal--incentive-mini-game {
  --modal-padding-x: 0;
  --modal-padding-y: 0;
}

.modal--incentive-mini-game .close-icon {
  z-index: 1;
}

.modal.modal--fancy-bg {
  position: relative;
  -webkit-mask-image: -webkit-radial-gradient(white, black);
}

.modal.modal--fancy-bg::before {
  content: "";
  position: absolute;
  background: var(--fancy-bg, none);
  filter: var(--fancy-bg-filter, none);
  z-index: -1;

  /* a bit of magic to hide blur artifacts on edges */
  top: -10%;
  left: -10%;
  width: 120%;
  height: 120%;
}

.modal--fancy-bg .modal-buttons::before {
  display: none;
}

.close-icon:focus {
  outline-color: var(--ui-color-focus);
}
